import { Injectable } from "@angular/core";
import { LocationStateStore, RideOpsSettings } from "./location-state.store";
import {
	Downtime,
	IsClearedForOperationDetails,
	LastQueueTimeUpdate,
	LastRidersUpdate,
	NoteTemplateReference,
	DowntimeTemplateReference,
	NoteTypeReference,
	RidePosition,
	Location,
	LocationState,
} from "./location-state.store";
import { NavController } from "@ionic/angular";
import { BehaviorSubject, combineLatest, filter, from, Subject, switchMap } from "rxjs";
import { TranslocoService } from "@ngneat/transloco";
import { LanguageService } from "../services/language.service";
import { AlertService } from "../services/alert.service";
import { IdleService } from "../services/idle.service";
import { Router } from "@angular/router";
import { select } from "@ngneat/elf";
import { retryBackoff } from "backoff-rxjs";
import {
	ApiService,
	assignmentConfigurationPolicyType,
	DowntimeTemplate,
	ExternalDispatchEntry,
	ExternalQueueTimeEntry,
} from "../services/api.service";
import { RaygunService } from "../services/raygun.service";
import { KeyWarningModalService } from "source/app/modals/key-warning/key-warning-modal.service";
import { ClosingTimeCheckService } from "../services/closing-time-check.service";
import { AssetDispatchService } from "../services/asset-dispatch.service";
import { UnitsInUsePromptService } from "../services/units-in-use-prompt.service";
import { RideOpsUiService } from "../services/rideops-ui.service";
import { UiQuery } from "./ui.query";

@Injectable({ providedIn: "root" })
export class LocationStateService {
	constructor(
		private locationStateStore: LocationStateStore,
		private navController: NavController,
		private translocoService: TranslocoService,
		private apiService: ApiService,
		private languageService: LanguageService,
		private raygunService: RaygunService,
		private alertService: AlertService,
		private idleService: IdleService,
		private router: Router,
		private keyWarningModalService: KeyWarningModalService,
		private closingTimeCheckService: ClosingTimeCheckService,
		private assetDispatchService: AssetDispatchService,
		private uiQuery: UiQuery,
		private unitsInUsePrompt: UnitsInUsePromptService,
		private rideOpsUi: RideOpsUiService,
	) {
		this.listenForLanguageChanges();
	}

	private isActivated = false;

	readonly ttlConfig = 240000; // #937 - Increased to 4 minutes to ease the server load for now.
	private cached$ = new BehaviorSubject(false);
	private cacheTimer: ReturnType<typeof setTimeout>;

	private readonly assetQueueRotationSubject = new Subject<{
		direction: "forward" | "back";
		occupiedSeats?: number;
	}>();
	readonly assetQueueRotation$ = this.assetQueueRotationSubject.asObservable();

	resetCache = () => {
		clearTimeout(this.cacheTimer);
		this.cached$.next(false);
	};

	private setCached = () => {
		this.cached$.next(true);
		this.cacheTimer = setTimeout(() => {
			this.resetCache();
		}, this.ttlConfig);
	};

	private isBefore2AMCurrentDay = (date: Date): boolean => {
		const now = new Date();
		const today2AM = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 2, 0, 0);
		return date < today2AM;
	};

	private shouldResetRiders = (state: LocationState): boolean => {
		// If the last riders update is older than 2 am, we think the ride is opening for the first time today
		if (state.riders > 0 && state.lastRidersUpdate?.lastUpdated) {
			const lastUpdated = new Date(state.lastRidersUpdate.lastUpdated);
			return this.isBefore2AMCurrentDay(lastUpdated);
		}
		return false;
	};

	openLocation = async () => {
		const priorState = this.locationStateStore.store.getValue();
		const armedAfterDowntime = this.unitsInUsePrompt.consumeAfterDowntimeArm();
		const shouldPromptUnitsInUse =
			this.rideOpsUi.isV2Active() &&
			(armedAfterDowntime || this.shouldPromptUnitsInUseOnOpen(priorState));

		try {
			await this.apiService.open();
			this.locationStateStore.store.update((state) => {
				const shouldResetRiders = this.shouldResetRiders(state);
				const riders = shouldResetRiders ? 0 : state.riders;
				const dispatches = shouldResetRiders ? 0 : state.dispatches;

				return {
					...state,
					state: "Open",
					riders,
					dispatches,
					downtime: null,
					lastAssetDispatchOccupiedSeats: undefined,
					// Mark the start of this ride-open session so the dispatch-stats "session" counter
					// measures dispatches from this open, not the previous one.
					locationOpenSessionStart: new Date().toISOString(),
				};
			});
			await this.navController.navigateRoot("dashboard");
			if (shouldPromptUnitsInUse) {
				this.unitsInUsePrompt.requestPresent();
				this.unitsInUsePrompt.nudgePresent();
				setTimeout(() => this.unitsInUsePrompt.nudgePresent(), 300);
			}
		} catch {
			if (armedAfterDowntime) {
				this.unitsInUsePrompt.armAfterDowntimeResolve();
			}
			this.alertService.showErrorWhileOpeningLocationAlert();
		}
	};

	private shouldPromptUnitsInUseOnOpen = (state: LocationState): boolean => {
		if (state.downtime != null) {
			return true;
		}

		// Never successfully opened this location on this device (cleared on location switch).
		if (state.locationOpenSessionStart == null) {
			return true;
		}

		const stateLastUpdated = state.stateLastUpdated;
		if (stateLastUpdated == null) {
			return true;
		}

		const lastUpdated = new Date(stateLastUpdated);
		if (Number.isNaN(lastUpdated.getTime())) {
			return true;
		}

		const openingTime = state.openingTime;
		if (openingTime == null) {
			return this.isBefore2AMCurrentDay(lastUpdated);
		}

		const opening = new Date(openingTime);
		if (Number.isNaN(opening.getTime())) {
			return this.isBefore2AMCurrentDay(lastUpdated);
		}

		return lastUpdated.getTime() < opening.getTime();
	};

	closeLocation = async (dispatches: number, riders: number) => {
		try {
			// Asset rides already write one Raven DispatchEntry per unit tap. A close-time
			// location dispatch would invent an extra entry without an asset_dispatches row, so
			// omit it. Non-asset rides keep the legacy catch-up dispatch (gate false).
			const suppressLegacyDispatch = await this.assetDispatchService.isAssetDispatchActive();
			await this.apiService.close(dispatches, riders, suppressLegacyDispatch);
			this.locationStateStore.store.update((state) => ({
				...state,
				state: "Closed",
				riders: 0,
				dispatches: 0,
				rideOpsSettings: {
					...state.rideOpsSettings,
					positions: this.removeAllAttendantsOnPositions(state.rideOpsSettings.positions),
				},
			}));
		} catch {
			this.alertService.showErrorWhileClosingLocationAlert();
		}
	};

	createDowntimeAndCloseLocation = (
		downtimeId: string,
		description: string,
		downTimeTemplate: DowntimeTemplate | DowntimeTemplateReference | null,
	) => {
		const closeDate = new Date();

		this.locationStateStore.store.update((state) => {
			const rideState = this.resolveDowntimeRideState(downTimeTemplate, state.createdDowntimesAreBlocking);
			const positions = state.rideOpsSettings.signoutAllAttendantsOnDowntimeCreation
				? this.removeAllAttendantsOnPositions(state.rideOpsSettings.positions)
				: state.rideOpsSettings.positions;

			return {
				...state,
				state: "Closed",
				downtime: {
					description,
					updated: closeDate.toISOString(),
					state: rideState,
					id: downtimeId,
				},
				rideOpsSettings: {
					...state.rideOpsSettings,
					positions,
				},
			};
		});

		this.resetCache();
	};

	resolveDowntime = async () => {
		try {
			const downtimeResolved = await this.apiService.resolveDowntime();
			if (downtimeResolved) {
				this.resetCache();
			} else {
				await this.alertService.showErrorWhileResolvingDowntimeAlert();
			}
		} catch {
			await this.alertService.showErrorWhileResolvingDowntimeAlert();
		}
	};

	private resolveDowntimeRideState = (
		template: DowntimeTemplate | DowntimeTemplateReference | null,
		createdDowntimesAreBlocking: boolean,
	): "Open" | "Blocking" => {
		const policy = template?.assignmentConfigurationPolicyType;

		if (policy === assignmentConfigurationPolicyType.Create) {
			return "Blocking";
		}

		if (policy === assignmentConfigurationPolicyType.DoNotCreate) {
			return "Open";
		}

		// No template, or policy is Inherit/undefined — use location default
		return createdDowntimesAreBlocking ? "Blocking" : "Open";
	};

	private removeAllAttendantsOnPositions = (positions: RidePosition[]): RidePosition[] => {
		return positions.map((posistion) => <RidePosition>{ ...posistion, attendant: null });
	};

	setRideOpsKey = (key: string): void => {
		if (key?.length <= 0) {
			console.warn("Attempted to set rideops key that was null or empty.");
			return;
		}

		this.locationStateStore.store.update((state) => ({ ...state, apiKey: key }));
	};

	setLocation = (location: Location) => {
		this.locationStateStore.store.update((state) => ({
			...state,
			location: location,
			locationOpenSessionStart: undefined,
		}));
		this.resetCache();
	};

	setCurrentOperator = (operator: Operator): void => {
		const loggedIn = new Date().toISOString();
		this.locationStateStore.store.update((state) => ({ ...state, operator: { ...operator, loggedIn } }));
		this.resetCache();
	};

	setAttendantOnPosition = (attendant: Attendant, position: RidePosition) => {
		const newRidePosition: RidePosition = {
			...position,
			attendant: {
				user: {
					id: attendant.id,
					name: attendant.name,
					profileImage: attendant.profileImage,
				},
				loggedIn: new Date().toISOString(),
			},
		};

		this.locationStateStore.store.update((state) => ({
			...state,
			rideOpsSettings: {
				...state.rideOpsSettings,
				positions: state.rideOpsSettings.positions.map((x) =>
					x.identifier === position.identifier ? newRidePosition : x,
				),
			},
		}));
	};

	removeAttendantOnPosition = (position: RidePosition) => {
		const newRidePosition: RidePosition = {
			...position,
			attendant: null,
		};

		this.locationStateStore.store.update((state) => ({
			...state,
			rideOpsSettings: {
				...state.rideOpsSettings,
				positions: state.rideOpsSettings.positions.map((x) =>
					x.identifier === position.identifier ? newRidePosition : x,
				),
			},
		}));
	};

	setPinForOperator = (pin: string): void => {
		this.locationStateStore.store.update((state) => ({
			...state,
			operator: { ...state.operator, pin: pin, pinCodeUsed: true },
			operators: [
				...state.operators.map((operator) => {
					if (operator.id === state.operator.id) {
						return { ...operator, pinCodeUsed: true };
					}
					return operator;
				}),
			],
		}));
	};

	clearOperator = async (skipNavigate = false) => {
		this.locationStateStore.store.update((state) => ({
			...state,
			operator: undefined,
			rideOpsSettings: {
				...state.rideOpsSettings,
				positions: this.removeAllAttendantsOnPositions(state.rideOpsSettings.positions),
			},
		}));

		if (skipNavigate === true) {
			return false;
		}

		return await this.navController.navigateRoot("operators");
	};

	updateDispatchesAndRiders = (lastUpdated: Date, riders: number, dispatches = 1): void => {
		this.locationStateStore.store.update((state) => ({
			...state,
			lastRidersUpdate: {
				lastUpdated: lastUpdated,
				riders: riders,
			},
			riders: state.riders + riders,
			dispatches: state.dispatches + dispatches,
		}));
	};

	revertDispatchesAndRiders = (
		riders: number,
		dispatches = 1,
		previousLastRidersUpdate?: LastRidersUpdate,
	): void => {
		this.locationStateStore.store.update((state) => ({
			...state,
			riders: Math.max(0, state.riders - riders),
			dispatches: Math.max(0, state.dispatches - dispatches),
			lastRidersUpdate: previousLastRidersUpdate,
		}));
	};

	setAssetUnitOrderOverride = (unitIds: string[]): void => {
		this.locationStateStore.store.update((state) => {
			const locationId = state.location?.id;
			if (!locationId) {
				return state;
			}

			return {
				...state,
				assetUnitOrderByLocationId: {
					...(state.assetUnitOrderByLocationId ?? {}),
					[locationId]: unitIds,
				},
			};
		});
	};

	resetAssetDispatchQueueIndex = (): void => {
		this.locationStateStore.store.update((state) => ({
			...state,
			assetDispatchQueueIndex: 0,
		}));
	};

	// Advances the asset dispatch queue carousel by one unit (just dispatched ← current ← next),
	// wrapping back to the first after the last. Called once per real per-unit asset dispatch
	// (see ActionsService.addCreateAssetDispatchAction). Index is into the available (not unavailable)
	// unit list so it stays aligned with `AssetDispatchService.currentUnit$`.
	advanceAssetDispatchQueueIndex = (occupiedSeats: number): void => {
		const unavailable = new Set(this.uiQuery.getSnapshot().unavailableUnitIds ?? []);

		this.assetQueueRotationSubject.next({ direction: "forward", occupiedSeats });
		this.locationStateStore.store.update((state) => {
			const unitCount = (state.rideOpsSettings.units ?? []).filter((unit) => !unavailable.has(unit.id)).length;
			if (unitCount <= 0) {
				return state;
			}

			const currentIndex = (((state.assetDispatchQueueIndex ?? 0) % unitCount) + unitCount) % unitCount;

			return {
				...state,
				assetDispatchQueueIndex: (currentIndex + 1) % unitCount,
				lastAssetDispatchOccupiedSeats: occupiedSeats,
			};
		});
	};

	revertHeldAssetDispatch = (
		riders: number,
		options: {
			previousLastOccupiedSeats?: number;
			previousLastRidersUpdate?: LastRidersUpdate;
			unitId?: string;
			previousUnitCount?: number | null;
		} = {},
	): void => {
		const unavailable = new Set(this.uiQuery.getSnapshot().unavailableUnitIds ?? []);

		this.assetQueueRotationSubject.next({ direction: "back", occupiedSeats: riders });
		this.locationStateStore.store.update((state) => {
			const unitCount = (state.rideOpsSettings.units ?? []).filter((unit) => !unavailable.has(unit.id)).length;
			const currentIndex =
				unitCount > 0 ? (((state.assetDispatchQueueIndex ?? 0) % unitCount) + unitCount) % unitCount : 0;

			const nextCounts = { ...(state.lastAssetDispatchCountByUnitId ?? {}) };
			if (options.unitId) {
				if (options.previousUnitCount == null) {
					delete nextCounts[options.unitId];
				} else {
					nextCounts[options.unitId] = options.previousUnitCount;
				}
			}

			return {
				...state,
				riders: Math.max(0, state.riders - riders),
				dispatches: Math.max(0, state.dispatches - 1),
				lastRidersUpdate: options.previousLastRidersUpdate,
				assetDispatchQueueIndex:
					unitCount > 0 ? (currentIndex - 1 + unitCount) % unitCount : state.assetDispatchQueueIndex,
				lastAssetDispatchOccupiedSeats:
					options.previousLastOccupiedSeats !== undefined
						? options.previousLastOccupiedSeats
						: state.lastAssetDispatchOccupiedSeats,
				lastAssetDispatchCountByUnitId: nextCounts,
			};
		});
	};

	setDispatchCooldownUntil = (until: string | undefined): void => {
		this.locationStateStore.store.update((state) => ({
			...state,
			dispatchCooldownUntil: until,
		}));
	};

	// Remembers the occupied-seat count just dispatched for a unit, keyed by unit id, so the dispatch
	// widget can restore it the next time that unit is "in action" instead of starting from 0. Stored
	// in persisted location state alongside the queue index, following the same pattern.
	setLastAssetDispatchCountForUnit = (unitId: string, count: number | null): void => {
		if (!unitId) {
			return;
		}

		this.locationStateStore.store.update((state) => {
			const next = { ...(state.lastAssetDispatchCountByUnitId ?? {}) };
			if (count == null) {
				delete next[unitId];
			} else {
				next[unitId] = count;
			}
			return {
				...state,
				lastAssetDispatchCountByUnitId: next,
			};
		});
	};

	updateQueueTime = (lastUpdated: Date, queueTime: number): void => {
		this.locationStateStore.store.update((state) => ({
			...state,
			lastQueueUpdate: {
				lastUpdated: lastUpdated,
				time: queueTime,
			},
		}));
	};
	updateExternalDispatch = (externalDispatch: ExternalDispatchEntry): void => {
		if (!externalDispatch?.entry) {
			return;
		}

		this.locationStateStore.store.update((state) => ({
			...state,
			lastExternalDispatch: {
				lastUpdated: new Date(),
				timestamp: new Date(externalDispatch.timestamp),
				riders: externalDispatch.entry.riders ?? 0,
				utilization: externalDispatch.entry.utilization ?? 0,
			},
		}));
	};
	updateExternalQueueTime = (queueTimeEntry: ExternalQueueTimeEntry): void => {
		if (!queueTimeEntry?.entry) {
			return;
		}

		this.locationStateStore.store.update((state) => ({
			...state,
			lastExternalQueueTime: {
				lastUpdated: new Date(),
				timestamp: new Date(queueTimeEntry.timestamp),
				queueTimeInMinutes: queueTimeEntry.entry.queueTimeInMinutes ?? 0,
				queueSize: queueTimeEntry.entry.queueSize ?? 0,
			},
		}));
	};
	setRideOpsState = (
		lastQueueTimeUpdate: LastQueueTimeUpdate,
		lastRidersUpdate: LastRidersUpdate,
		riders: number,
		dispatches: number,
	): void => {
		this.locationStateStore.store.update((state) => ({
			...state,
			lastQueueUpdate: lastQueueTimeUpdate,
			lastRidersUpdate: lastRidersUpdate,
			riders: riders,
			dispatches: dispatches,
		}));
	};

	resetRideOpsState = (): void => {
		this.locationStateStore.store.update((state) => ({
			...state,
			lastQueueUpdate: undefined,
			lastRidersUpdate: undefined,
			riders: 0,
			dispatches: 0,
		}));
	};

	private listenForLanguageChanges = () => {
		this.locationStateStore.store
			.pipe(select((state) => ({ operator: state.operator, language: state.language })))
			.subscribe((languageAndOperator) => {
				const availableLanguages = this.languageService.getAvailableLangs().map((x) => x.id);

				if (availableLanguages.includes(languageAndOperator.operator?.language)) {
					this.translocoService.setActiveLang(languageAndOperator.operator.language);
				} else if (availableLanguages.includes(languageAndOperator.language)) {
					this.translocoService.setActiveLang(languageAndOperator.language);
				} else {
					this.translocoService.setActiveLang("en");
				}
			});
	};

	public updateLocationStatus = async (location: string): Promise<void> => {
		try {
			const response = await this.apiService.locationStatus(location);

			this.setCached();

			// Response data can be null, if key no longer gives acces to location or is deleted
			if (response === null) {
				const newKey = await this.keyWarningModalService.showWarningForKey(location);
				if (newKey != null && newKey.length > 0) {
					this.setRideOpsKey(newKey);
					return await this.updateLocationStatus(location);
				}
				return;
			}
			this.keyWarningModalService.CloseKeyWarningIfShown();

			this.locationStateStore.store.update((state) => {
				// We do some state change checks here if its the same locations
				if (state.location?.id == location) {
					if (state.state == "Open" && response.state != "Open" && this.router.url == "/dashboard") {
						this.alertService.alertLocationNoLongerOpen();
					}

					if (
						state.isClearedForOperationDetails.isClearedForOperation &&
						!response.isClearedForOperationDetails.isClearedForOperation &&
						this.router.url == "/dashboard"
					) {
						this.alertService.alertNoLongerClearedForOperation();
					}

					// Downtime cleared via status (local resolve, assignment finished elsewhere, etc.).
					if (state.downtime != null && response.downtime == null) {
						this.unitsInUsePrompt.armAfterDowntimeResolve();
					}
				}

				return {
					...state,
					...response,
					lastServerUpdate: new Date().toISOString(),
					lastUpdateFailed: false,
					location: {
						id: response.id,
						name: response.name,
						language: response.language,
						scannerCode: response.scannerCode,
						barcodeSignIn: state.location?.barcodeSignIn,
					},
				};
			});

			// Check if closing time reminder should be shown
			await this.closingTimeCheckService.checkAndTriggerClosingTimeReminder();
		} catch (error: unknown) {
			this.locationStateStore.store.update((state) => ({ ...state, lastUpdateFailed: true }));

			// Skip logging for connection and timeout errors
			const isHttpError = typeof error === "object" && error !== null && "status" in error;
			if (isHttpError && (error.status === 0 || error.status === 504)) {
				throw error;
			}

			if (error instanceof Error) {
				this.raygunService.send(error, {});
			} else {
				this.raygunService.send(new Error("An unknown error occured during updateLocationStatus."), {
					error: error,
				});
			}

			throw error;
		}
	};

	public updateLocationOperators = async (location: string): Promise<void> => {
		try {
			const response = await this.apiService.locationOperators(location);

			this.setCached();

			if (response === null) {
				const newKey = await this.keyWarningModalService.showWarningForKey(location);
				if (newKey != null && newKey.length > 0) {
					this.setRideOpsKey(newKey);
					return await this.updateLocationOperators(location);
				}
				return;
			}
			this.keyWarningModalService.CloseKeyWarningIfShown();

			this.locationStateStore.store.update((state) => ({
				...state,
				operators: response.operators,
				requiredCompetencies: response.requiredCompetencies,
				language: response.language,
				location: {
					id: response.id,
					name: response.name,
					language: response.language,
					scannerCode: response.scannerCode,
					barcodeSignIn: response.barcodeSignIn,
				},
				state: response.state,
				lastServerUpdate: new Date().toISOString(),
				lastUpdateFailed: false,
			}));
		} catch (error: unknown) {
			this.locationStateStore.store.update((state) => ({ ...state, lastUpdateFailed: true }));

			const isHttpError = typeof error === "object" && error !== null && "status" in error;
			if (isHttpError && (error.status === 0 || error.status === 504)) {
				throw error;
			}

			if (error instanceof Error) {
				this.raygunService.send(error, {});
			} else {
				this.raygunService.send(new Error("An unknown error occured during updateLocationOperators."), {
					error: error,
				});
			}

			throw error;
		}
	};

	setSnapshot = (snapshot: LocationState) => {
		this.locationStateStore.store.update((state) => ({ ...state, ...snapshot }));
	};

	activate() {
		if (this.isActivated) {
			return;
		}

		this.isActivated = true;

		combineLatest([
			this.cached$,
			this.locationStateStore.store.pipe(select((state) => state.location)),
			this.idleService.isInactive$,
		])
			.pipe(
				filter(
					([isCached, location, isInactive]) => isCached == false && isInactive == false && location != null,
				),
				switchMap(([_, location]) => {
					const operator = this.locationStateStore.store.query((state) => state.operator);
					return from(
						operator != null
							? this.updateLocationStatus(location.id)
							: this.updateLocationOperators(location.id),
					);
				}),
				retryBackoff({
					initialInterval: 100,
					maxInterval: 60000,
					resetOnSuccess: true,
				}),
			)
			.subscribe();
	}
}

export interface ServerStateResponse {
	createdDowntimesAreBlocking: boolean;
	downtime: Downtime;
	state: "Open" | "Closed";
	stateLastUpdated: string;
	operators: Operator[];
	requiredCompetencies: DocumentReference[];
	preopeningChecklist: string;
	rideOpsSettings: RideOpsSettingsWithDeviceSettings;
	locations: Location[];
	locationProperties?: string[];
	unitsAvailable: number;
	unitCapacity: number;
	language: string;
	isClearedForOperation: boolean;
	isClearedForOperationDetails: IsClearedForOperationDetails;
	noteTypes: NoteTypeReference[];
	noteTemplates: NoteTemplateReference[];
	id: string;
	name: string;
	scannerCode: string;
	openingTime?: string;
	closingTime?: string;
	organizationDateTimeSettings?: DateTimeSettings;
	// Note: rideOpsSettings.units[] carries the asset-based dispatch unit list when configured.
}

export interface RideOpsSettingsWithDeviceSettings extends RideOpsSettings {
	deviceSettings: UIDeviceSettings;
}

export interface UIDeviceSettings {
	allowChangingDispatchesWhenClosingLocation: boolean;
	allowChangingRidersWhenClosingLocation: boolean;
	showQueueTimeWidget: boolean;
	showDispatchWidget: boolean;
	maximumRidersPerDispatch: number;
	dispatchWidgetType: "selector" | "grid";
	dispatchGridNumberDirection: "ascending" | "descending";
	dispatchMetric: "emptySeats" | "occupiedSeats";
	showSafariAiDispatchWidget: boolean;
	showSafariAiQueueTimeWidget: boolean;
	showBulletinWidget: boolean;
}
